import React, { useCallback, useEffect, useRef } from "react";

import fpLogo from "@/assets/fp-logo-v1.png";
import { readSessionMessage } from "@/services/embedSession";
import { setSessionToken } from "@/services/sessionToken";

const POPUP_NAME = "fp-embed-signin";
const POPUP_FEATURES = "width=520,height=680,menubar=no,toolbar=no";

/**
 * The signed-out state of an embedded frame (#11021).
 *
 * The frame cannot sign anyone in itself — a cross-site frame cannot read the
 * `SameSite=Lax` session cookie, and SSO would need to navigate the frame to an
 * identity provider that refuses to be framed. So this offers one button, which
 * opens a top-level popup where the ordinary sign-in flow runs unchanged.
 *
 * The popup hands the session back by postMessage. We check `event.origin`
 * against our own origin on the way in (see `readSessionMessage`), so the page
 * hosting this frame — a different origin — can neither read the handover nor
 * forge one.
 */
const EmbedSignInPrompt: React.FC = () => {
  const popupRef = useRef<Window | null>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent<unknown>) => {
      const token = readSessionMessage(event, window.location.origin);
      if (!token) return;
      setSessionToken(token);
      popupRef.current = null;
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const openSignIn = useCallback(() => {
    // Focus an already-open popup rather than stacking a second one.
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }
    popupRef.current = window.open(
      "/embed/auth/complete",
      POPUP_NAME,
      POPUP_FEATURES
    );
  }, []);

  return (
    <div className="flex h-full w-full items-center justify-center bg-white p-8">
      <div className="flex max-w-sm flex-col items-center gap-4 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-[10px] bg-[#ECEDEF]">
          <img src={fpLogo} alt="" className="h-6 w-6 object-contain" />
        </div>
        <div className="text-base font-semibold text-[#10141B]">
          Sign in to view this record
        </div>
        <div className="font-mono text-xs text-[#6E7480]">
          Opens a Finpilot window. Your sign-in stays between you and Finpilot.
        </div>
        <button
          type="button"
          onClick={openSignIn}
          className="cursor-pointer rounded-md bg-gray-800 px-4 py-2 text-sm text-white hover:bg-gray-700"
        >
          Sign in to Finpilot
        </button>
      </div>
    </div>
  );
};

export default EmbedSignInPrompt;
